import type { MeasurementEntry } from '@fasting-timer/shared'

import type { MeasurementsApi, MeasurementUpsertOutcome } from './measurementsApi'

const PUSH_DEBOUNCE_MS = 1500
const CURSOR_STORAGE_KEY = 'measurementsSyncCursor'

type MeasurementsSyncDeps = {
   api: Pick<MeasurementsApi, 'list' | 'upsert'>
   getPendingEntries: () => MeasurementEntry[]
   applyRemote: (entries: readonly MeasurementEntry[]) => void
   markPushed: (
      results: readonly MeasurementUpsertOutcome[],
      entries: readonly MeasurementEntry[],
   ) => void
   /** Storage for the delta cursor; defaults to localStorage. */
   storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
   /** Scheduler for the debounced push; defaults to `setTimeout`. */
   schedule?: (callback: () => void, delayMs: number) => () => void
}

/**
 * Returns a scheduler backed by `setTimeout`.
 *
 * @param callback - The function to run.
 * @param delayMs - The delay.
 * @returns A cancel function.
 */
function defaultSchedule(callback: () => void, delayMs: number) {
   const timerId = setTimeout(callback, delayMs)
   return () => clearTimeout(timerId)
}

/**
 * Creates the measurements sync runtime: pushes pending local entries (debounced
 * after edits, in batches) and pulls server changes since the last `serverTime`.
 * Pull results are merged through the store, so a stale local edit is replaced by
 * the server's version and a newer one stays pending until the next push.
 *
 * @param deps - Store accessors, the API client, and optional storage/scheduler.
 * @returns The sync methods.
 */
export function createMeasurementsSync(deps: MeasurementsSyncDeps) {
   const storage =
      deps.storage ?? (typeof localStorage !== 'undefined' ? localStorage : null)
   const schedule = deps.schedule ?? defaultSchedule
   let cancelScheduledPush: (() => void) | null = null
   let inFlight: Promise<void> | null = null
   let rerunRequested = false

   /**
    * Reads the delta cursor.
    *
    * @returns The ISO cursor, or null for a full pull.
    */
   function readCursor(): string | null {
      try {
         return storage?.getItem(CURSOR_STORAGE_KEY) ?? null
      } catch {
         return null
      }
   }

   /**
    * Stores the delta cursor.
    *
    * @param value - The ISO cursor, or null to clear it.
    */
   function writeCursor(value: string | null) {
      try {
         if (value === null) {
            storage?.removeItem(CURSOR_STORAGE_KEY)
         } else {
            storage?.setItem(CURSOR_STORAGE_KEY, value)
         }
      } catch {
         // Storage unavailable: the next pull is a full pull, which is still correct.
      }
   }

   /**
    * Pushes every pending entry, then pulls changes since the cursor.
    */
   async function runSync() {
      const pending = deps.getPendingEntries()
      const responses = await deps.api.upsert(pending)
      if (responses) {
         for (const response of responses) {
            deps.markPushed(response.results, response.entries)
         }
      }
      const listed = await deps.api.list({ since: readCursor() })
      if (listed) {
         deps.applyRemote(listed.entries)
         writeCursor(listed.serverTime)
      }
   }

   /**
    * Runs a push-then-pull cycle, coalescing concurrent requests into one rerun.
    *
    * @returns A promise that settles when the cycle (and any rerun) completes.
    */
   function sync(): Promise<void> {
      if (inFlight) {
         rerunRequested = true
         return inFlight
      }
      inFlight = (async () => {
         try {
            do {
               rerunRequested = false
               await runSync()
            } while (rerunRequested)
         } finally {
            inFlight = null
         }
      })()
      return inFlight
   }

   /**
    * Schedules a debounced sync after a local edit.
    */
   function schedulePush() {
      cancelScheduledPush?.()
      cancelScheduledPush = schedule(() => {
         cancelScheduledPush = null
         sync().catch(error => {
            console.error('Failed to push measurements', error)
         })
      }, PUSH_DEBOUNCE_MS)
   }

   /**
    * Forgets the delta cursor so the next sync pulls everything (key change / reset).
    */
   function resetCursor() {
      cancelScheduledPush?.()
      cancelScheduledPush = null
      writeCursor(null)
   }

   /**
    * Returns whether a sync has something to do without a server change: a
    * first pull or unpushed local entries.
    *
    * @returns True when the next sync should run regardless of the change stamp.
    */
   function hasLocalWork() {
      return readCursor() === null || deps.getPendingEntries().length > 0
   }

   return { sync, schedulePush, resetCursor, hasLocalWork }
}
